<script lang="ts" setup>
import { useModal } from 'vue-final-modal'

import BasicButton from '~/components/form-elements/BasicButton.vue'
import NewStarRating from '~/components/form-elements/NewStarRating.vue'
import Auth from '~/components/modal-windows/Auth.vue'
import ConfirmDeletion from '~/components/modal-windows/ConfirmDeletion.vue'
import CreateOrEditReview from '~/components/modal-windows/CreateOrEditReview.vue'
import ProductGrayButton from '~/components/products/ProductGrayButton.vue'
import constants from '~/constants'
import { AuthAction } from '~/enums/auth-action'
import helpers from '~/helpers'
import { useAuthStore } from '~/store/auth'

const authStore = useAuthStore()

const props = defineProps<{
  isProductPurchasedByUser: boolean | null
  productId: string
  rating: number | null
  totalPublishedReviews: string | number | null
  userReviewId: string | null
}>()

function setUserReviewId (x: string | null): void {
  emit('setUserReviewId', x)
}

async function deleteReview (id: string): Promise<void> {
  const { failedToDeleteReview, reviewIsDeleted } = messages

  try {
    await $apiHelper.reviews.deleteReview(id)

    setUserReviewId(null)

    $toast.success(reviewIsDeleted)
  } catch (error) {
    console.error(error)

    $toast.error(failedToDeleteReview)
  }
}

async function openAuthModalWindow (): Promise<void> {
  await useModal({ component: Auth, attrs: { action: AuthAction.EnterLogin } }).open()
}

async function openConfirmDeletionModalWindow (): Promise<void> {
  await useModal({
    component: ConfirmDeletion,
    attrs: {
      title: constants.confirmDeletionTexts.review,
      onConfirmDeletion: async (): Promise<void> => deleteReview(props.userReviewId!)
    }
  }).open()
}

async function openCreateOrEditReviewModalWindow (): Promise<void> {
  const { productId, userReviewId } = props

  await useModal({
    component: CreateOrEditReview,
    attrs: { productId, reviewId: userReviewId, onSetUserReviewId: setUserReviewId }
  }).open()
}
</script>

<template>
  <div v-if="rating" class="mb-4 rounded-xl md:bg-[#F6F6F6] md:p-6">
    <div class="mb-2 flex">
      <strong
        class="mr-2 text-[24px] font-medium leading-[1.1] tracking-[-0.03em] text-main-dark md:mr-3 md:text-[40px]"
      >
        {{ parseFloat(rating.toFixed(1)) }}
      </strong>

      <NewStarRating :rating="rating" />
    </div>

    <div
      v-if="totalPublishedReviews"
      class="text-base leading-normal tracking-[-0.01em] text-main-dark md:text-sm"
    >
      {{ totalPublishedReviews }}&nbsp;{{ helpers.getDeclension(totalPublishedReviews, constants.wordForms.review) }}
    </div>
  </div>

  <template v-if="authStore.isUser">
    <template v-if="isProductPurchasedByUser">
      <ProductGrayButton
        class="mb-3 !w-full"
        :title="userReviewId === null ? 'Написать отзыв' : 'Изменить отзыв'"
        @click="openCreateOrEditReviewModalWindow"
      />

      <BasicButton
        v-if="userReviewId !== null"
        color="red"
        is-small
        title="Удалить отзыв"
        @click="openConfirmDeletionModalWindow"
      />
      <Notification v-else status="info">
        Чтобы написать отзыв, необходимо приобрести товар.
      </Notification>
    </template>
  </template>
  <a
    v-else
    class="
      -mt-1 inline-block w-full text-nowrap rounded-2xl bg-main-gray-low py-4 text-center text-base font-medium
      leading-normal tracking-[-0.01em] text-main-green !no-underline
    "
    href="#"
    @click.prevent="openAuthModalWindow"
  >
    Войдите, чтобы написать отзыв
  </a>
</template>
